import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from 'react';

import { CanvasScaleContext, DESIGN_CANVAS } from '@/lib/canvas';
import { cn } from '@/lib/utils';

interface Props {
  /** Classes for the measured wrapper (the visible "card"/frame). */
  className?: string;
  style?: CSSProperties;
  /** Background painted on the design surface itself. */
  background?: string;
  /** Rendered inside the scaled design surface (1280px wide design space). */
  children: ReactNode;
  /**
   * Rendered at UI scale (not scaled) on top of the card — for editing chrome
   * that should keep a constant pixel size regardless of zoom (label, add-row).
   */
  overlay?: ReactNode;
  /** Forwarded to the wrapper element (e.g. a dnd-kit droppable ref). */
  wrapperRef?: Ref<HTMLDivElement>;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

/**
 * Renders its children inside a fixed-**width** (1280px) design surface and
 * scales that surface to the available width via a CSS transform. The surface
 * is at least 16:9 (720px tall) but **grows with its content** — a content-heavy
 * slide expands downward instead of being clipped, and the parent scroll
 * container lets the user reach it. Children draw in pure design-space px; the
 * scale is transparent to them (freeform drag reads `CanvasScaleContext`).
 */
export function ScaledStage({
  className,
  style,
  background,
  children,
  overlay,
  wrapperRef,
  onClick,
}: Props) {
  const measureRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState<number>(DESIGN_CANVAS.height);

  useLayoutEffect(() => {
    const wrap = measureRef.current;
    const surface = surfaceRef.current;
    if (!wrap || !surface) return;

    const update = () => {
      const w = wrap.clientWidth;
      if (w > 0) setScale(w / DESIGN_CANVAS.width);
      // scrollHeight is the unscaled (layout) content height — the transform
      // doesn't affect it, so it's the natural design-space height of the slide.
      setContentHeight(Math.max(DESIGN_CANVAS.height, surface.scrollHeight));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    ro.observe(surface);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      ref={mergeRefs(measureRef, wrapperRef)}
      onClick={onClick}
      style={{ height: contentHeight * scale, ...style }}
      className={cn('relative w-full overflow-hidden', className)}
    >
      <CanvasScaleContext.Provider value={scale}>
        <div
          ref={surfaceRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: DESIGN_CANVAS.width,
            minHeight: DESIGN_CANVAS.height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            background,
          }}
        >
          {children}
        </div>
        {overlay}
      </CanvasScaleContext.Provider>
    </div>
  );
}

/** Merge an internal ref with an optional forwarded ref onto one node. */
function mergeRefs<T>(
  internal: React.MutableRefObject<T | null>,
  external?: Ref<T>,
): (node: T | null) => void {
  return (node) => {
    internal.current = node;
    if (typeof external === 'function') external(node);
    else if (external) (external as React.MutableRefObject<T | null>).current = node;
  };
}
